import * as React from 'react';
import Select from 'react-select';

import { translate } from 'components/translate-hoc';
import Loader from '@sitegen/bo/client/src/modules/loader/loader.component';
import Checkbox from 'components/checkbox';

import './platform-selection.styles.less';

import { IPlatformSelectionProps } from './platform-selection.types';
import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';

import { TEXT_KEYS, PLATFORM_REGISTRATION_ANALYTICS } from './platform-selection.constants';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

export class PlatformSelection extends React.Component<
IPlatformSelectionProps,
IFRPlatformSelectionState
> {
    public state: IFRPlatformSelectionState = {
        selectedPlatform: null,
        displayValidationWarning: false
    };

    public handleSubmit = (): void => {
        const { selectedPlatform } = this.state;
        const { submitCallback } = this.props;

        if (!selectedPlatform) {
            return this.setState({ displayValidationWarning: true });
        }

        submitCallback(selectedPlatform);
        AnalyticsService.sendClickEvent(PLATFORM_REGISTRATION_ANALYTICS(selectedPlatform));
    };

    public handleDesktopSelectorChange = (value: string): () => void => {
        return () => {
            if (PLATFORM_TYPES[value]) {
                this.setState({
                    selectedPlatform: PLATFORM_TYPES[value]
                });
            }
        };
    };

    public handleMobileSelectorChange = (result: { value: string, label: string }): void => {
        if (PLATFORM_TYPES[result.value]) {
            this.setState({
                selectedPlatform: PLATFORM_TYPES[result.value]
            });
        }
    };

    private renderPlatformItem = (platformName: PLATFORM_TYPES): JSX.Element => {
        const isChecked = platformName === this.state.selectedPlatform;
        return (
            <Checkbox
                name={platformName.toLowerCase()}
                label={platformName}
                checked={isChecked}
                key={platformName}
                onChange={this.handleDesktopSelectorChange(platformName)}
            />
        );
    };

    private renderMobilePlatformSelector = (): JSX.Element => {
        const { selectedPlatform } = this.state;
        const { platforms } = this.props;

        const ownedPlatforms = Object.keys(platforms).reduce((acc, platform) => (
            platforms[platform]
                ? [...acc, { value: platform, label: platform }]
                : acc
        ), []);

        const currentValue = selectedPlatform
            ? { value: selectedPlatform as string, label: selectedPlatform as string }
            : null;

        return (
            <div className="sitegen-fr-platform-selection__select">
                <Select
                    options={ownedPlatforms}
                    value={currentValue}
                    onChange={this.handleMobileSelectorChange}
                    className="sitegen-fr-platform-selection__dropdown"
                    classNamePrefix="sitegen-fr-platform-selection"
                />
            </div>
        );
    };

    private renderDesktopPlatformSelector = (): JSX.Element => {
        const ownedPlatforms = Object.keys(this.props.platforms).filter(key => this.props.platforms[key]);

        return (
            <ul className="sitegen-fr-platform-selection__list">
                {ownedPlatforms.map(this.renderPlatformItem)}
            </ul>
        );
    };

    private renderWarningMessage = (): JSX.Element | null => {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const validationErrorText = translateKey(TEXT_KEYS.VALIDATION_ERROR);
        const classList = ['sitegen-fr-platform-selection__validation-warning'];

        if (this.state.displayValidationWarning) {
            classList.push('sitegen-fr-platform-selection__validation-warning--visible');
        }

        return (
            <div className={classList.join(' ')}>
                {validationErrorText}
            </div>
        );
    };

    private renderContent = (): JSX.Element => {
        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const pageTitleText = translateKey(TEXT_KEYS.PAGE_TITLE);
        const pageDescriptionText = translateKey(TEXT_KEYS.PAGE_DESCRIPTION);
        const submitButtonText = translateKey(TEXT_KEYS.SUBMIT_BUTTON);

        return (
            <div className="sitegen-fr-platform-selection">
                <div className="sitegen-fr-platform-selection__caption">
                    {translateKey(TEXT_KEYS.CAPTION)}
                </div>
                <div className="sitegen-fr-platform-selection__title">{pageTitleText}</div>

                <div className="sitegen-fr-platform-selection__content">
                    <div className="sitegen-fr-platform-selection__content-description">{pageDescriptionText}</div>
                    {this.renderMobilePlatformSelector()}
                    {this.renderDesktopPlatformSelector()}
                    <button
                        className="sitegen-fr-platform-selection__confirm-button"
                        onClick={this.handleSubmit}
                    >
                        {submitButtonText}
                    </button>
                    {this.renderWarningMessage()}
                </div>
            </div>
        );
    };

    public render(): JSX.Element {
        return this.props.isLoading ? <Loader/> : this.renderContent();
    }
}

interface IFRPlatformSelectionState {
    readonly selectedPlatform: null | PLATFORM_TYPES;
    readonly displayValidationWarning: boolean;
}

export default translate()(PlatformSelection);
